// EBITDABridgeDetailed.tsx
import React from 'react';
import { AccountEntry, CategoryOption, EBITDARole, Project } from '../../types';
import { getEBITDADisplayLabel } from '../../lib/financial/ebitdaUtils';
import {
  calculateTTM,
  calculateTTMForPeriodType,
  calculateCategoryTotal,
  compareMonthYear
} from '../../lib/financial/calculations';

interface EBITDABridgeDetailedProps {
  accounts: AccountEntry[];
  periods: string[];
  viewType: 'monthly' | 'quarterly' | 'yearly';
  allMonths: string[];
  calculateNetIncome: (period: string) => number;
  calculateNetRevenueTotal: (period: string) => number;
  ttmNetIncome?: number;
  templateCategories?: CategoryOption[];
  calculateTTMNetRevenue: () => number;
  selectedProject?: Project | null;
}

// Helper function to get all categories with a specific EBITDA role
const getCategoriesByEBITDARole = (
  templateCategories: CategoryOption[] | undefined,
  role: EBITDARole
): string[] => {
  if (!templateCategories) return [];
  return templateCategories
    .filter(category => category.ebitdaRole === role)
    .map(category => category.value);
};

export const EBITDABridgeDetailed: React.FC<EBITDABridgeDetailedProps> = ({
  accounts,
  periods,
  viewType,
  allMonths,
  calculateNetIncome,
  calculateNetRevenueTotal,
  ttmNetIncome,
  templateCategories,
  calculateTTMNetRevenue,
  selectedProject
}) => {
  // Define EBITDA roles and their labels
  const ebitdaRoles: Array<{role: EBITDARole, label: string}> = [
    { role: 'interest-expense', label: 'Add: Interest Expense (Income)' },
    { role: 'income-tax-expense', label: 'Add: Income Tax Expense' },
    { role: 'depreciation', label: 'Add: Depreciation' },
    { role: 'amortization', label: 'Add: Amortization' }
  ];

  // Legacy category names for backward compatibility
  const legacyEBITDACategories = [
    { value: 'Interest Expense (Income)', role: 'interest-expense' as EBITDARole },
    { value: 'Income Tax Expense', role: 'income-tax-expense' as EBITDARole },
    { value: 'Depreciation', role: 'depreciation' as EBITDARole },
    { value: 'Amortization', role: 'amortization' as EBITDARole }
  ];

  // Check if any categories have EBITDA roles assigned
  const hasAnyEBITDARoles = (): boolean => {
    if (!templateCategories) return false;
    return templateCategories.some(cat => cat.ebitdaRole !== undefined && cat.ebitdaRole !== null);
  };
  
  // Determine if we should use legacy category names or role-based approach
  const shouldUseLegacyCategories = !hasAnyEBITDARoles();

  // Function to check if at least one EBITDA category exists in the template
  const hasAnyEBITDACategory = (): boolean => {
    if (hasAnyEBITDARoles()) {
      // Check if at least one category with an EBITDA role exists
      return ebitdaRoles.some(({role}) => {
        return templateCategories?.some(cat => cat.ebitdaRole === role);
      });
    } else {
      // Fall back to checking if legacy category names exist
      return legacyEBITDACategories.some(item => {
        return templateCategories?.some(cat => cat.value === item.value || cat.label === item.value);
      });
    }
  };

  // Get last 12 months for TTM calculations
  const getLast12Months = () => {
    const sortedMonths = [...allMonths].sort(compareMonthYear);
    return sortedMonths.slice(Math.max(0, sortedMonths.length - 12));
  };

  // Function to calculate total for a specific EBITDA role in a period
  const calculateRoleTotal = (role: EBITDARole, period: string): number => {
    if (!templateCategories) return 0;
    
    if (shouldUseLegacyCategories) {
      // Use legacy category names
      const legacyCategoryName = legacyEBITDACategories.find(item => item.role === role)?.value || '';
      return calculateCategoryTotal(
        accounts,
        legacyCategoryName,
        period,
        viewType,
        false,
        false
      );
    } else {
      // Use role-based approach
      const categoriesWithRole = getCategoriesByEBITDARole(templateCategories, role);
      
      // Sum up the values for all categories with this role
      return categoriesWithRole.reduce((total, category) => {
        return total + calculateCategoryTotal(
          accounts,
          category,
          period,
          viewType,
          false, // Is it revenue?
          false  // Is it contra-revenue?
        );
      }, 0);
    }
  };

  // Function to calculate TTM total for a specific EBITDA role
  const calculateRoleTTM = (role: EBITDARole): number => {
    if (!templateCategories) return 0;
    
    const last12Months = getLast12Months();
    const periodType = selectedProject?.period_type || 'monthly';
    
    if (shouldUseLegacyCategories) {
      // Use legacy category names
      const legacyCategoryName = legacyEBITDACategories.find(item => item.role === role)?.value || '';
      return calculateTTMForPeriodType(
        accounts,
        legacyCategoryName,
        last12Months,
        false,
        false,
        periodType
      );
    } else {
      // Use role-based approach
      const categoriesWithRole = getCategoriesByEBITDARole(templateCategories, role);
      
      return categoriesWithRole.reduce((total, category) => {
        return total + calculateTTMForPeriodType(
          accounts,
          category,
          last12Months,
          false,
          false,
          periodType
        );
      }, 0);
    }
  };

  // Calculate EBITDA for a period using role-based approach
  const calculateEBITDA = (period: string, netIncome: number): number => {
    let ebitda = netIncome;
    
    // Add back each EBITDA component
    ebitdaRoles.forEach(({role}) => {
      ebitda += calculateRoleTotal(role, period);
    });
    
    return ebitda;
  };

  // Calculate TTM EBITDA using role-based approach
  const calculateTTMEBITDA = (ttmNetIncome: number): number => {
    let ttmEBITDA = ttmNetIncome;
    
    // Add back each EBITDA component
    ebitdaRoles.forEach(({role}) => {
      ttmEBITDA += calculateRoleTTM(role);
    });
    
    return ttmEBITDA;
  };

  // If no EBITDA categories exist in the template, don't render the bridge
  if (!hasAnyEBITDACategory()) {
    return null;
  }

  return (
    <>
      {/* EBITDA Bridge Section Header */}
      <tr className="bg-gray-50">
        <td className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-sm font-semibold text-gray-900 border-r border-gray-200">
          EBITDA Bridge
        </td>
        <td className="px-6 py-4 text-sm font-semibold" />
        <td colSpan={periods.length + (viewType === 'yearly' ? 1 : 0)} />
      </tr>

      {/* Add-back rows */}
      {ebitdaRoles.map(({role, label}) => {
        // Check if we have any categories with this role
        const hasRole = shouldUseLegacyCategories
          ? templateCategories?.some(cat => cat.value === legacyEBITDACategories.find(item => item.role === role)?.value || cat.label === legacyEBITDACategories.find(item => item.role === role)?.value)
          : templateCategories?.some(cat => cat.ebitdaRole === role);
        
        // Skip roles that don't exist in the template
        if (!hasRole) return null;
        
        return (
          <tr key={role}>
            <td className="sticky left-0 z-10 bg-gray-50 px-6 py-4 text-sm text-gray-900 border-r border-gray-200">
              {label}
            </td>
            <td className="px-6 py-4 text-sm font-semibold" />
            {periods.map(period => (
              <td key={period} className="px-6 py-4 text-sm text-gray-900 text-right">
                {calculateRoleTotal(role, period).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </td>
            ))}
            {viewType === 'yearly' && (
              <td className="px-6 py-4 text-sm text-gray-900 text-right">
                {calculateRoleTTM(role).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </td>
            )}
          </tr>
        );
      })}

      {/* EBITDA Total */}
      <tr className="bg-gray-300">
        <td className="sticky left-0 z-10 bg-gray-300 px-6 py-4 text-sm font-semibold text-gray-900 border-r border-gray-200">
          EBITDA
        </td>
        <td className="px-6 py-4 text-sm font-semibold" />
        {periods.map(period => {
          const netIncome = calculateNetIncome(period);
          const ebitda = calculateEBITDA(period, netIncome);
          
          return (
            <td key={period} className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
              {ebitda.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}
            </td>
          );
        })}
        {viewType === 'yearly' && ttmNetIncome !== undefined && (
          <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
            {calculateTTMEBITDA(ttmNetIncome).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            })}
          </td>
        )}
      </tr>

      {/* EBITDA % */}
      <tr className="bg-gray-100">
        <td className="sticky left-0 z-10 bg-gray-100 px-6 py-4 text-sm font-semibold text-gray-900 border-r border-gray-200">
          EBITDA % of Net Sales
        </td>
        <td className="px-6 py-4 text-sm font-semibold" />
        {periods.map(period => {
          const netSales = calculateNetRevenueTotal(period);
          const netIncome = calculateNetIncome(period);
          const ebitda = calculateEBITDA(period, netIncome);
          const ebitdaPercent = netSales !== 0 ? (ebitda / netSales) * 100 : 0;
          
          return (
            <td key={period} className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
              {ebitdaPercent.toFixed(1)}%
            </td>
          );
        })}
        {viewType === 'yearly' && ttmNetIncome !== undefined && (
          <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
            {(() => {
              const ttmNetRevenue = calculateTTMNetRevenue();
              const ttmEBITDA = calculateTTMEBITDA(ttmNetIncome);
              const ttmEbitdaPercent = ttmNetRevenue !== 0 ? (ttmEBITDA / ttmNetRevenue) * 100 : 0;
              return `${ttmEbitdaPercent.toFixed(1)}%`;
            })()}
          </td>
        )}
      </tr>
    </>
  );
};
